const NAME_REGEX = /^[a-zA-Z]+([ '-][a-zA-Z]+)*$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
const USERNAME_REGEX = /^[a-zA-Z0-9]{8,15}$/;
const PASSWORD_REGEX =
  /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{8,24}$/;

// Secret text:
// - 2 to 50 characters
// - letters, numbers, and spaces only
// - must contain at least one letter
const SECRET_TEXT_REGEX = /^[a-zA-Z0-9 ]{2,50}$/;
const SECRET_HAS_LETTER_REGEX = /[a-zA-Z]/;

// --------------------
// Normalizers
// --------------------

export const capitalizeName = (value = "") => {
  return value
    .trim()
    .toLowerCase()
    .split(/([ '-])/)
    .map((part) => {
      if (part === " " || part === "-" || part === "'") return part;
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join("");
};

export const normalizeName = (value = "") => capitalizeName(value);

export const normalizeEmail = (value = "") => value.trim().toLowerCase();

export const normalizeUsername = (value = "") => value.trim().toLowerCase();

export const normalizePhone = (value = "") => value.replace(/[^\d+]/g, "");

export const normalizePassword = (value = "") => value.trim();

export const normalizeSecretString = (value = "") => value.trim();

// --------------------
// Validators
// --------------------

export const isValidName = (value = "") => {
  const trimmed = value.trim();
  return (
    trimmed.length >= 2 && trimmed.length <= 30 && NAME_REGEX.test(trimmed)
  );
};

export const isValidEmail = (value = "") => {
  const normalized = normalizeEmail(value);
  return (
    EMAIL_REGEX.test(normalized) &&
    normalized.length >= 6 &&
    normalized.length <= 72
  );
};

export const isValidUsername = (value = "") => {
  return USERNAME_REGEX.test(value.trim());
};

export const isValidPhone = (value = "") => {
  return /^\+[1-9]\d{8,17}$/.test(normalizePhone(value));
};

export const isValidPassword = (value = "") => {
  const trimmed = normalizePassword(value);
  return (
    PASSWORD_REGEX.test(trimmed) && !trimmed.toLowerCase().includes("password")
  );
};

export const isValidSecretString = (value = "") => {
  const trimmed = normalizeSecretString(value);
  return (
    trimmed.length >= 2 &&
    trimmed.length <= 50 &&
    SECRET_TEXT_REGEX.test(trimmed) &&
    SECRET_HAS_LETTER_REGEX.test(trimmed)
  );
};

// --------------------
// Formatting helpers
// --------------------

export const formatName = (value = "") =>
  isValidName(value) ? normalizeName(value) : value.trim();

export const formatEmail = (value = "") => normalizeEmail(value);

export const formatUsername = (value = "") => normalizeUsername(value);

export const formatPhone = (value = "") => normalizePhone(value);

export const formatPassword = (value = "") => normalizePassword(value);

export const formatSecretString = (value = "") =>
  normalizeSecretString(value);

// --------------------
// UID helpers
// --------------------

export const detectUidType = (value = "") => {
  if (isValidEmail(value)) return "email";
  if (isValidPhone(value)) return "phone";
  if (isValidUsername(value)) return "username";
  return null;
};

export const isValidUidByType = (type, value = "") => {
  switch (type) {
    case "email":
      return isValidEmail(value);
    case "phone":
      return isValidPhone(value);
    case "username":
      return isValidUsername(value);
    default:
      return false;
  }
};

export const formatUidByType = (type, value = "") => {
  switch (type) {
    case "email":
      return formatEmail(value);
    case "phone":
      return formatPhone(value);
    case "username":
      return formatUsername(value);
    default:
      return value;
  }
};

// --------------------
// Default export
// --------------------

const userCredentialsUtils = {
  capitalizeName,
  normalizeName,
  normalizeEmail,
  normalizeUsername,
  normalizePhone,
  normalizePassword,
  normalizeSecretString,

  isValidName,
  isValidEmail,
  isValidUsername,
  isValidPhone,
  isValidPassword,
  isValidSecretString,

  formatName,
  formatEmail,
  formatUsername,
  formatPhone,
  formatPassword,
  formatSecretString,

  detectUidType,
  isValidUidByType,
  formatUidByType,
};

export default userCredentialsUtils;
